import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    DetailsListLayoutMode,
    Selection,
    IColumn,
} from "office-ui-fabric-react/lib/DetailsList";
import { MarqueeSelection } from "office-ui-fabric-react/lib/MarqueeSelection";
import { ShimmeredDetailsList, Stack } from "office-ui-fabric-react";
import { IOrdersListProps } from "../models/OrdersList.types";
import { footer, listS, listContainerS } from "../styles/OrdersList.styles";
import Pagination from "../providers/Pagination";
import { IRenderProps } from "../providers/Pagination.types";
import {
    setActiveOrder,
    setSelectedOrders,
} from "../../../config/redux/orders/ordersActions";
import { toggleOrderPanel } from "../../../config/redux/orders/ordersPanel/orderPanelActions"
import { generateColumns } from "../actions/generateColums";
import { NavigationPath } from "../../../config/routing/NavigationPath";
import { sort, toggleFiltration } from "../../../config/redux/filters/filterActions";

const OrdersList: React.FC<IOrdersListProps> = ({ pathname, isPanelOpen }) => {
    const { t } = useTranslation("admin");
    const dispatch = useDispatch();
    const { orderPanelOpen, activeOrder, selectedOrders, reloadOrders, orderCanceled, orderAccepted, remove } =
        useSelector((state: RootStateOrAny) => state.orders);

    // Default sort (newest first) must be flagged on the rendered columns, otherwise the first header click does nothing
    const buildColumns = () => {
        const columns: IColumn[] = generateColumns(pathname, t);
        const createdAtCol = columns.find(c => c.key === "CreatedAt");
        if (createdAtCol) {
            createdAtCol.isSortedDescending = true;
        }
        return columns;
    };

    const [cols, setCols] = useState<IColumn[]>(buildColumns);

    const footerRef = useRef<HTMLDivElement>(null);

    const selection = useRef(new Selection({
        onSelectionChanged: () => {
            if (!orderPanelOpen) {
                const items = selection.current.getSelection();
                dispatch(setSelectedOrders({ selectedOrders: [...items] }));
            }
        },
    }));

    useEffect(() => {
        setCols(buildColumns());
    }, [t, pathname]);

    useEffect(() => {
        const createdAtCol = cols.find(c => c.key === "CreatedAt");
        if (createdAtCol) {
            dispatch(sort({ column: createdAtCol }));
        }
    }, []);

    useEffect(() => {
        if (reloadOrders || remove || orderAccepted || orderCanceled || !selectedOrders[0]) {
            selection.current.selectToKey("", true);
        }
    }, [reloadOrders, remove, orderAccepted, orderCanceled, selectedOrders]);

    useEffect(() => {
        if (!activeOrder || !activeOrder.id) {
            selection.current.selectToKey("", true);
        }
    }, [activeOrder]);

    useEffect(() => {
        return () => {
            dispatch(setSelectedOrders({ selectedOrders: [] }));
        };
    }, []);

    const onItemInvoked = (item: any) => {
        if (pathname !== NavigationPath.ALL_ORDERS) {
            dispatch(setActiveOrder(item));
            dispatch(toggleOrderPanel(true));
        }
    };

    const onColumnHeaderClick = (_: any, column: any) => {
        const newColumns = cols.slice();
        const currColumn = newColumns.find(c => c.key === column.key)!;
        newColumns.forEach(col => {
            if (col === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                col.isSorted = false;
                col.isSortedDescending = true;
            }
        });
        dispatch(toggleFiltration({ filter: true }));
        dispatch(sort({ column: currColumn }));
        setCols(newColumns);
    };

    return (
        <Pagination
            footerRef={footerRef}
            render={({ data, loading }: IRenderProps) => (
                <>
                    <Stack styles={listContainerS}>
                        <MarqueeSelection isEnabled={!isPanelOpen} selection={selection.current}>
                            <ShimmeredDetailsList
                                items={data}
                                columns={cols}
                                setKey="items"
                                layoutMode={DetailsListLayoutMode.justified}
                                selection={selection.current}
                                selectionPreservedOnEmptyClick={true}
                                onItemInvoked={onItemInvoked}
                                usePageCache={true}
                                styles={listS}
                                onColumnHeaderClick={onColumnHeaderClick}
                                enableShimmer={loading}
                            />
                        </MarqueeSelection>
                    </Stack>
                    <div className={footer} ref={footerRef} />
                </>
            )}
        />
    );
};

export default OrdersList;